/**
 * LIVEMOA · BARCODE/WMS A4-1 (+A4-1C outbound) · Inbound/Outbound Scan Modal.
 *
 * Modes
 * -----
 *  - `mode="inbound"`  (default) · barcode → +1 · reason fixed to "입고 (바코드 스캔)"
 *  - `mode="outbound"` (A4-1C)   · barcode → -1 · seller picks a reason
 *                                   (불량 / 파손 / 폐기 / 창고반출 / 기타)
 *                                   · sends `reject_below_zero: true`
 *
 * REUSES:
 *   - `WarehouseScanInput` (A3) as the scanner foundation (USB/BT keyboard-wedge
 *     + camera BarcodeDetector) via `onSubmit` passthrough.
 *   - `GET /api/seller/inventory/scan-lookup` (READ ONLY) for barcode → variant.
 *   - `POST /api/seller/inventory/adjust` (existing engine) with the same
 *     `idempotency_key` idempotency contract used by inbound.  For outbound,
 *     `reject_below_zero:true` is opt-in and does NOT change legacy behaviour.
 *
 * LOCKS (unchanged):
 *   - inventory_transactions.source_ref unique index (idempotency).
 *   - stock alert / low_stock_threshold logic.
 *   - manual 재고조정 modal (`AdjustModal`) — completely untouched.
 *   - A3 seller Fulfillment scan flow — no shared endpoint.
 */
import React, { useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { X, Package, CheckCircle2, XCircle, ArrowDownCircle, ArrowUpCircle } from 'lucide-react';
import api from '@/lib/api';
import WarehouseScanInput from '@/components/operator/WarehouseScanInput';
// A4-1D · 매입처 선택 (입고 모드에서만 상단 노출).
import SupplierPicker from '@/components/seller/SupplierPicker';

const won = (n) => `${(Number(n) || 0).toLocaleString('ko-KR')}`;
const uuid = () => {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch {}
  return `sc-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const OUTBOUND_REASONS = ['불량', '파손', '폐기', '창고반출', '기타'];

export default function InboundScanModal({ onClose, onSaved, mode = 'inbound' }) {
  const isOutbound = mode === 'outbound';
  const sessionIdRef = useRef(uuid());
  const seqRef = useRef(0);
  const [scanned, setScanned] = useState({});
  const [events, setEvents] = useState([]);
  const [lastCard, setLastCard] = useState(null);
  const codeSeqRef = useRef(new Map());
  // A4-1C · outbound reason picker (mandatory for outbound mode)
  const [outReason, setOutReason] = useState(OUTBOUND_REASONS[0]);
  // A4-1D · session-scoped supplier (INBOUND only) → attached to every adjust
  // call so all scans in this session share the same supplier snapshot.
  const [sessionSupplier, setSessionSupplier] = useState(null);

  const pushEvent = (kind, msg, key) => {
    setEvents((prev) => [{
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      ts: new Date(), kind, msg, key,
    }, ...prev].slice(0, 20));
  };

  const delta = isOutbound ? -1 : 1;

  const handleSubmit = async (rawCode) => {
    const c = String(rawCode || '').trim();
    if (!c) throw new Error('스캔 코드가 없습니다.');

    // Step 1 · Lookup (READ)
    let lk;
    try {
      const r = await api.get('/seller/inventory/scan-lookup', { params: { code: c } });
      lk = r.data;
    } catch (e) {
      const detail = e?.response?.data?.detail || '등록되지 않은 상품 바코드입니다.';
      pushEvent('error', `✕ ${detail}  (${c})`);
      setLastCard({ tone: 'error', msg: detail, code: c });
      throw new Error(detail);
    }
    if (!lk?.product_id) {
      const msg = '등록되지 않은 상품 바코드입니다.';
      pushEvent('error', `✕ ${msg}  (${c})`);
      setLastCard({ tone: 'error', msg, code: c });
      throw new Error(msg);
    }

    // Frontend-side pre-check for outbound so 0-stock scans don't even go
    // through the network.  Server ALSO enforces (reject_below_zero:true) —
    // this is UX, not the source of truth.
    if (isOutbound && (lk.total_stock || 0) <= 0) {
      const msg = '재고가 0입니다. 반출할 수 없습니다.';
      pushEvent('error', `✕ ${msg} · ${lk.product_name}`);
      setLastCard({ tone: 'error', msg, code: c });
      throw new Error(msg);
    }

    // Step 2 · Idempotent adjust ±1
    let seq = codeSeqRef.current.get(c);
    if (seq === undefined) {
      seqRef.current += 1;
      seq = seqRef.current;
      codeSeqRef.current.set(c, seq);
    }
    const idempotency_key = `${sessionIdRef.current}:${seq}`;
    let adj;
    const reason = isOutbound
      ? `반출 (바코드 스캔 · ${outReason})`
      : '입고 (바코드 스캔)';
    try {
      const r = await api.post('/seller/inventory/adjust', {
        product_id: lk.product_id,
        variant_key: lk.variant_key,
        delta,
        reason,
        memo: `scan_session=${sessionIdRef.current} seq=${seq} code=${c}`,
        sku: lk.sku || undefined,
        idempotency_key,
        ...(isOutbound ? { reject_below_zero: true } : {}),
        // A4-1D · attach session-scoped supplier to every INBOUND write.
        // Outbound never sends supplier (LOCK per spec §10).
        ...((!isOutbound && sessionSupplier) ? { supplier_id: sessionSupplier } : {}),
      });
      adj = r.data;
    } catch (e) {
      const detail = e?.response?.data?.detail
        || (isOutbound ? '반출 처리에 실패했습니다.' : '입고 처리에 실패했습니다.');
      pushEvent('error', `✕ ${detail}`);
      setLastCard({ tone: 'error', msg: detail, code: c });
      throw new Error(detail);
    }

    // Update local per-variant counter
    const key = `${lk.product_id}::${lk.variant_key}`;
    setScanned((prev) => {
      const cur = prev[key] || {
        product_id: lk.product_id, variant_key: lk.variant_key,
        product_name: lk.product_name, option_snapshot: lk.option_snapshot,
        sku: lk.sku, barcode: lk.barcode,
        base_stock: lk.total_stock, session_qty: 0, current_stock: lk.total_stock,
      };
      const next = { ...cur };
      if (adj?.idempotent) {
        pushEvent('warn', `⟳ 중복 스캔 무시 (${cur.product_name})`, key);
      } else {
        next.session_qty = (cur.session_qty || 0) + 1;
        next.current_stock = adj?.after ?? Math.max(0, cur.current_stock + delta);
      }
      const state = { ...prev, [key]: next };
      setLastCard({
        tone: 'ok', product_name: lk.product_name, sku: lk.sku,
        variant_key: lk.variant_key, option_snapshot: lk.option_snapshot,
        session_qty: next.session_qty, before: cur.base_stock, after: next.current_stock,
        idempotent: !!adj?.idempotent,
        reason: isOutbound ? outReason : null,
      });
      if (!adj?.idempotent) {
        pushEvent('ok',
          `${isOutbound ? '↓' : '↑'} ${isOutbound ? '반출 -1' : '입고 +1'} · `
          + `${lk.product_name} · 이번 ${next.session_qty}, 현재재고 ${next.current_stock}`,
          key);
      }
      return state;
    });

    return { matched: true };
  };

  const total = useMemo(() =>
    Object.values(scanned).reduce((a, r) => a + (r.session_qty || 0), 0),
    [scanned]);

  const close = () => {
    if (total > 0 && typeof onSaved === 'function') onSaved();
    onClose();
  };

  const tone = isOutbound ? 'amber' : 'red';
  const AccentIcon = isOutbound ? ArrowDownCircle : ArrowUpCircle;

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4"
         data-testid={isOutbound ? 'inv-outbound-scan-modal' : 'inv-inbound-scan-modal'}
         onClick={close}>
      <div className="w-full sm:max-w-lg bg-white dark:bg-[#141414] rounded-t-2xl sm:rounded-2xl max-h-[92vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white dark:bg-[#141414] border-b border-black/10 dark:border-white/10 p-4 flex justify-between items-center">
          <div className="min-w-0">
            <p className="text-lg font-black flex items-center gap-1.5">
              <AccentIcon size={18} className={isOutbound ? 'text-amber-600' : 'text-[#FF3B30]'} />
              {isOutbound ? '반출 스캔' : '입고 스캔'}
            </p>
            <p className="text-[11px] text-gray-500 mt-0.5">
              {isOutbound
                ? '바코드 스캔 = 즉시 -1 · 사유 선택 필수 · 재고 0 미만 불가'
                : '바코드 스캔 = 즉시 +1 · 기존 재고조정과 별개의 편의 흐름'}
            </p>
          </div>
          <button onClick={close} className="p-1"
                  data-testid={isOutbound ? 'inv-outbound-close' : 'inv-inbound-close'}><X size={16}/></button>
        </div>

        <div className="p-4 space-y-3">
          {/* A4-1D · Inbound session supplier picker (once for the whole session).
              Outbound mode HIDES this per spec §10 LOCK. */}
          {!isOutbound && (
            <div className="rounded-xl border border-black/10 dark:border-white/10 p-3"
                 data-testid="inv-inbound-supplier-wrap">
              <p className="text-[11px] font-black uppercase tracking-widest text-gray-600 dark:text-gray-300 mb-1.5">
                이번 입고 매입처 (선택 사항)
              </p>
              <SupplierPicker value={sessionSupplier} onChange={setSessionSupplier}
                              testId="inv-inbound-supplier" />
              <p className="mt-1.5 text-[10px] text-gray-500">
                한 번 선택하면 이번 세션의 모든 입고 기록에 함께 저장됩니다.
              </p>
            </div>
          )}
          {/* A4-1C · Outbound reason selector */}
          {isOutbound && (
            <div className="rounded-xl border border-amber-300/60 bg-amber-50/60 dark:bg-amber-500/10 p-3"
                 data-testid="inv-outbound-reason-wrap">
              <p className="text-[11px] font-black uppercase tracking-widest text-amber-800 dark:text-amber-300 mb-1.5">
                반출 사유
              </p>
              <div className="flex flex-wrap gap-1.5">
                {OUTBOUND_REASONS.map((r) => (
                  <button key={r} onClick={() => setOutReason(r)}
                          data-testid={`inv-outbound-reason-${r}`}
                          className={`px-3 py-1.5 text-xs font-bold rounded-full border transition ${
                            outReason === r
                              ? 'bg-amber-500 text-white border-amber-500'
                              : 'border-amber-300/60 text-amber-800 dark:text-amber-200 bg-white/50 dark:bg-black/20'
                          }`}>
                    {r}
                  </button>
                ))}
              </div>
              <p className="mt-1.5 text-[10px] text-amber-700 dark:text-amber-300">
                모든 반출은 <b>{outReason}</b> 사유로 재고이력에 기록됩니다.
              </p>
            </div>
          )}

          <div className={`rounded-2xl border-2 p-4 ${
            !lastCard ? 'border-dashed border-black/10 dark:border-white/10 text-gray-500'
              : lastCard.tone === 'ok'
                ? (isOutbound
                    ? 'border-amber-400 bg-amber-50 dark:bg-amber-500/10 text-amber-900 dark:text-amber-200'
                    : 'border-emerald-400 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-900 dark:text-emerald-200')
                : 'border-red-400 bg-red-50 dark:bg-red-500/10 text-red-900 dark:text-red-200'
            }`}
            data-testid={isOutbound ? 'inv-outbound-card' : 'inv-inbound-card'}>
            {!lastCard ? (
              <div className="text-center py-6">
                <Package size={24} className="mx-auto opacity-40" />
                <p className="mt-2 text-sm font-bold">
                  {isOutbound ? '반출 바코드를 스캔하세요' : '입고 바코드를 스캔하세요'}
                </p>
                <p className="text-[11px] mt-1">상품 · SKU · 옵션이 자동 식별됩니다.</p>
              </div>
            ) : lastCard.tone === 'ok' ? (
              <div>
                <div className="flex items-center gap-1 text-[11px] font-black uppercase tracking-wider">
                  <CheckCircle2 size={14}/>
                  {lastCard.idempotent
                    ? '중복 스캔 무시'
                    : (isOutbound ? '반출 처리 완료' : '입고 처리 완료')}
                </div>
                <p className="text-base font-black mt-1"
                   data-testid={isOutbound ? 'inv-outbound-last-name' : 'inv-inbound-last-name'}>
                  {lastCard.product_name || '(이름 없음)'}
                </p>
                <p className="text-[11px] mt-0.5 opacity-80">
                  {lastCard.variant_key === 'default' ? '기본' : lastCard.variant_key} · SKU {lastCard.sku || '-'}
                  {isOutbound && lastCard.reason && <span> · 사유 <b>{lastCard.reason}</b></span>}
                </p>
                <div className="grid grid-cols-3 gap-2 mt-3 text-center">
                  <div className="rounded-lg bg-white/40 dark:bg-black/20 p-2">
                    <p className="text-[10px] font-black uppercase opacity-70">이번 {isOutbound ? '반출' : '입고'}</p>
                    <p className="text-xl font-black mt-0.5"
                       data-testid={isOutbound ? 'inv-outbound-session-qty' : 'inv-inbound-session-qty'}>
                      {isOutbound ? '-' : '+'}{won(lastCard.session_qty)}
                    </p>
                  </div>
                  <div className="rounded-lg bg-white/40 dark:bg-black/20 p-2">
                    <p className="text-[10px] font-black uppercase opacity-70">이전 재고</p>
                    <p className="text-xl font-black mt-0.5">{won(lastCard.before)}</p>
                  </div>
                  <div className="rounded-lg bg-white/40 dark:bg-black/20 p-2">
                    <p className="text-[10px] font-black uppercase opacity-70">{isOutbound ? '반출 후' : '입고 후'}</p>
                    <p className="text-xl font-black mt-0.5"
                       data-testid={isOutbound ? 'inv-outbound-after' : 'inv-inbound-after'}>
                      {won(lastCard.after)}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center">
                <XCircle size={24} className="mx-auto" />
                <p className="text-sm font-black mt-2"
                   data-testid={isOutbound ? 'inv-outbound-error-msg' : 'inv-inbound-error-msg'}>
                  {lastCard.msg || '오류'}
                </p>
                {lastCard.code && (
                  <p className="text-[11px] font-mono mt-1 opacity-80">코드: {lastCard.code}</p>
                )}
              </div>
            )}
          </div>

          <WarehouseScanInput
            sid={sessionIdRef.current}
            onSubmit={handleSubmit}
            onReset={() => setLastCard(null)}
          />

          <div className="rounded-xl border border-black/10 dark:border-white/10 p-3"
               data-testid={isOutbound ? 'inv-outbound-summary' : 'inv-inbound-summary'}>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-black">
                이번 세션 {isOutbound ? '반출' : '입고'} · 합계
                <span className={`ml-1 ${isOutbound ? 'text-amber-600' : 'text-[#FF3B30]'}`}
                      data-testid={isOutbound ? 'inv-outbound-total' : 'inv-inbound-total'}>
                  {isOutbound ? '-' : '+'}{won(total)}
                </span>
              </p>
              <span className="text-[10px] text-gray-500 font-mono">
                {sessionIdRef.current.slice(0, 12)}…
              </span>
            </div>
            {Object.keys(scanned).length === 0 ? (
              <p className="text-[11px] text-gray-500">아직 스캔된 상품이 없습니다.</p>
            ) : (
              <div className="space-y-1">
                {Object.entries(scanned).map(([k, r]) => (
                  <div key={k} className="flex items-center justify-between text-[11px] py-1 border-b border-black/5 dark:border-white/10 last:border-0"
                       data-testid={`${isOutbound ? 'inv-outbound-row' : 'inv-inbound-row'}-${r.product_id}-${r.variant_key}`}>
                    <div className="min-w-0 truncate">
                      <span className="font-bold">{r.product_name}</span>
                      <span className="text-gray-500 ml-1">· {r.variant_key === 'default' ? '기본' : r.variant_key}</span>
                    </div>
                    <div className="text-right shrink-0 ml-2 font-mono">
                      <span className={`${isOutbound ? 'text-amber-600' : 'text-[#FF3B30]'} font-black`}>
                        {isOutbound ? '-' : '+'}{r.session_qty}
                      </span>
                      <span className="text-gray-400 mx-1">·</span>
                      <span>{won(r.current_stock)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {events.length > 0 && (
            <div className="rounded-xl border border-black/10 dark:border-white/10 p-3 max-h-40 overflow-y-auto"
                 data-testid={isOutbound ? 'inv-outbound-events' : 'inv-inbound-events'}>
              <p className="text-[10px] font-black uppercase text-gray-500 mb-1">이벤트</p>
              {events.map((e) => (
                <div key={e.id}
                     className={`text-[11px] py-0.5 ${
                       e.kind === 'ok' ? 'text-emerald-700 dark:text-emerald-300'
                       : e.kind === 'warn' ? 'text-amber-700 dark:text-amber-300'
                       : 'text-red-700 dark:text-red-300'}`}>
                  {e.msg}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-white dark:bg-[#141414] border-t border-black/10 dark:border-white/10 p-3 flex gap-2">
          <button onClick={close}
                  className="flex-1 py-3 rounded-full border border-black/10 dark:border-white/10 text-xs font-black"
                  data-testid={isOutbound ? 'inv-outbound-done' : 'inv-inbound-done'}>
            닫기
          </button>
        </div>
      </div>
    </div>
  );
}
